import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * Smart Install Prompt
 *
 * Heuristically nudges interested visitors to install the PWA:
 *  - Counts visits (24h window) in localStorage; show after 3+ visits in 7 days
 *  - OR if current session crosses 2-minute dwell time, show
 *  - Dismissals (X button) suppress for 7 days
 *  - "Maybe later" suppresses for 24h
 *  - Hidden if already installed (display-mode: standalone)
 *  - Hidden if browser doesn't expose beforeinstallprompt AND not iOS Safari
 */
const VISITS_KEY = "smart_pwa_visits";
const DISMISSED_KEY = "smart_pwa_dismissed_until";
const SHOWN_KEY = "smart_pwa_shown_this_session";
const DWELL_MS = 2 * 60 * 1000; // 2 minutes

function loadVisits() {
  try {
    const raw = localStorage.getItem(VISITS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((t) => typeof t === "number") : [];
  } catch {
    return [];
  }
}

function recordVisit() {
  const now = Date.now();
  let arr = loadVisits();
  // Throttle: only count a "new visit" once per 12h
  const TWELVE_H = 12 * 3600 * 1000;
  if (!arr.length || now - arr[arr.length - 1] > TWELVE_H) {
    arr.push(now);
  }
  // Keep only last 30 days
  const cutoff = now - 30 * 24 * 3600 * 1000;
  arr = arr.filter((t) => t >= cutoff);
  localStorage.setItem(VISITS_KEY, JSON.stringify(arr));
  return arr;
}

function visitsInLastWeek(arr) {
  const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
  return arr.filter((t) => t >= cutoff).length;
}

function isSuppressed() {
  const until = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
  return until > Date.now();
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  const ua = (navigator.userAgent || "").toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && !/crios|fxios/.test(ua);
}

export default function SmartInstallPrompt() {
  const [show, setShow] = useState(false);
  const [showIOS, setShowIOS] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    if (isStandalone() || isSuppressed()) return;
    if (sessionStorage.getItem(SHOWN_KEY)) return;
    if (typeof window !== "undefined") {
      const p = window.location.pathname || "";
      if (
        p.startsWith("/admin") ||
        p.startsWith("/login") ||
        p.startsWith("/auth")
      ) {
        return;
      }
    }

    const visits = recordVisit();
    const weekCount = visitsInLastWeek(visits);

    const trigger = () => {
      if (!isSuppressed() && !sessionStorage.getItem(SHOWN_KEY)) {
        setShow(true);
        sessionStorage.setItem(SHOWN_KEY, "1");
      }
    };
    if (weekCount >= 3) {
      trigger();
      return;
    }
    const t = setTimeout(trigger, DWELL_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  if (!show) return null;
  const installable = !!installEvent || isIOSSafari();
  if (!installable) return null;

  const dismiss = (days = 7) => {
    const until = Date.now() + days * 24 * 3600 * 1000;
    localStorage.setItem(DISMISSED_KEY, String(until));
    setShow(false);
    setShowIOS(false);
  };

  const onInstall = async () => {
    if (installEvent && typeof installEvent.prompt === "function") {
      try {
        installEvent.prompt();
        const choice = await installEvent.userChoice;
        if (choice?.outcome === "accepted") {
          setShow(false);
          localStorage.setItem(DISMISSED_KEY, String(Date.now() + 365 * 24 * 3600 * 1000));
        }
      } catch {
        /* user dismissed */
      }
      return;
    }
    if (isIOSSafari()) {
      setShowIOS(true);
    }
  };

  return (
    <>
      <div
        className="fixed inset-x-3 bottom-3 md:inset-auto md:left-6 md:bottom-6 md:max-w-sm z-[9998] animate-fade-in"
        data-testid="smart-install-banner"
      >
        <div className="bg-[#111] border border-[#00E5FF]/30 rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,229,255,0.4)] p-4 backdrop-blur-xl">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 shrink-0 bg-gradient-to-br from-[#00E5FF] to-[#FF5E00] rounded-xl flex items-center justify-center">
              <Smartphone size={20} className="text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-white text-sm">
                Majte SuperMajstri po ruke
              </h4>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Pridajte si appku na plochu — rýchle dopyty, notifikácie o nových projektoch, offline prístup.
              </p>
            </div>
            <button
              onClick={() => dismiss(7)}
              className="w-7 h-7 shrink-0 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Zatvoriť"
              data-testid="smart-install-close"
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => dismiss(1)}
              className="flex-1 text-xs px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-zinc-300 hover:bg-white/10 transition-colors"
              data-testid="smart-install-later"
            >
              Neskôr
            </button>
            <button
              onClick={onInstall}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs px-3 py-2 bg-[#00E5FF] text-black font-bold rounded-lg hover:brightness-110 transition-all"
              data-testid="smart-install-yes"
            >
              <Download size={12} />
              Nainštalovať
            </button>
          </div>
        </div>
      </div>

      {showIOS && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setShowIOS(false)}
          data-testid="smart-install-ios-hint"
        >
          <div
            className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-sm p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOS(false)}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Zavrieť"
            >
              <X size={16} />
            </button>
            <div className="w-12 h-12 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-xl flex items-center justify-center mb-3">
              <Smartphone size={20} className="text-[#00E5FF]" />
            </div>
            <h3 className="font-black text-lg mb-2">Pridať na plochu</h3>
            <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
              Na iPhone / iPade nie je automatický install dialog. Appku si pridáte takto:
            </p>
            <ol className="space-y-2.5 text-xs">
              {[
                <>Klepnite na <strong>Zdieľať</strong> (dole v Safari).</>,
                <>Vyberte <strong>{`„Pridať na plochu"`}</strong> (Add to Home Screen).</>,
                <>Potvrďte názov <strong>{`„SuperMajstri"`}</strong> a klepnite <strong>Pridať</strong>.</>,
              ].map((txt, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#00E5FF] text-black text-[10px] font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-zinc-200">{txt}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
